// Typed client for the superuser admin surface. Mirrors the backend
// /v1/admin/* envelopes verbatim (flat shapes, no `{data: ...}` wrapper).
// Every endpoint here is gated server-side on user.is_superuser.

import { api } from '@/lib/api';
import type { TicketDetail, TicketStatus } from '@/lib/support';

export type OverviewPeriod = '24h' | '7d' | '30d';

export type PlanKey = 'free' | 'pro' | 'team' | 'enterprise';

export interface AdminOverview {
  period: OverviewPeriod;
  generated_at: string;
  users: {
    total: number;
    new_in_period: number;
    verified_total: number;
    by_plan: Record<PlanKey, number>;
  };
  activation: {
    activated_total: number;
    activation_rate: number; // 0..1
  };
  discoveries: {
    total: number;
    in_period: number;
    succeeded_in_period: number;
    failed_in_period: number;
    fail_rate_in_period: number; // 0..1
    avg_duration_ms: number | null;
    by_regime: Record<string, number>;
  };
  datasets: { total: number; in_period: number };
  knowledge: { total: number; in_period: number };
  api_keys: { active_total: number; in_period: number };
  revenue: {
    active_subscriptions: number;
    mrr_cents: number;
    by_plan: { pro: number; team: number; enterprise: number };
    payments_in_period_cents: number;
    payments_count_in_period: number;
  };
}

export type TimeseriesMetric = 'signups' | 'discoveries' | 'revenue' | 'active_users';

export type TimeseriesDays = 7 | 30 | 90;

export interface TimeseriesPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

export interface AdminTimeseries {
  metric: TimeseriesMetric;
  days: TimeseriesDays;
  points: TimeseriesPoint[]; // oldest -> newest, zero-filled
}

export async function fetchAdminOverview(period: OverviewPeriod): Promise<AdminOverview> {
  const resp = await api.get<AdminOverview>('/admin/overview', { params: { period } });
  return resp.data;
}

export async function fetchAdminTimeseries(
  metric: TimeseriesMetric,
  days: TimeseriesDays,
): Promise<AdminTimeseries> {
  const resp = await api.get<AdminTimeseries>('/admin/overview/timeseries', {
    params: { metric, days },
  });
  return resp.data;
}

// --- Traffic analytics ----------------------------------------------------
// Mirrors GET /v1/admin/traffic?days={7|30|90}. First-party pageview/session
// rollup; flat shape, no `{data: ...}` wrapper.

export type TrafficDays = 7 | 30 | 90;

export interface TrafficTimeseriesPoint {
  date: string; // YYYY-MM-DD
  pageviews: number;
  sessions: number;
}

export interface AdminTraffic {
  days: TrafficDays;
  totals: {
    pageviews: number;
    sessions: number;
    logged_in_pageviews: number;
  };
  timeseries: TrafficTimeseriesPoint[]; // oldest -> newest, zero-filled
  top_pages: Array<{ path: string; views: number }>;
  top_referrers: Array<{ referrer: string; views: number }>;
  sources: Array<{ source: string; sessions: number }>; // direct|organic|social|referral
  geo: Array<{ country: string; sessions: number }>;
  utm_campaigns: Array<{ campaign: string; sessions: number }>;
}

export async function fetchAdminTraffic(days: TrafficDays): Promise<AdminTraffic> {
  const resp = await api.get<AdminTraffic>('/admin/traffic', { params: { days } });
  return resp.data;
}

// --- User management ------------------------------------------------------

export type UserStatusFilter = 'all' | 'active' | 'suspended' | 'deleted';

// Row shape from GET /admin/users (the list envelope).
export interface AdminUserListItem {
  id: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  is_superuser: boolean;
  plan: PlanKey;
  suspended_at: string | null;
  deleted_at: string | null;
  created_at: string;
  last_login_at: string | null;
  discovery_count: number;
  dataset_count: number;
}

export interface AdminUsersPage {
  items: AdminUserListItem[];
  next_cursor: string | null;
}

export interface AdminUsersQuery {
  q?: string;
  status?: UserStatusFilter;
  cursor?: string;
  limit?: number;
}

export async function fetchAdminUsers(params: AdminUsersQuery): Promise<AdminUsersPage> {
  const resp = await api.get<AdminUsersPage>('/admin/users', {
    params: {
      q: params.q || undefined,
      status: params.status && params.status !== 'all' ? params.status : undefined,
      cursor: params.cursor || undefined,
      limit: params.limit ?? 20,
    },
  });
  return resp.data;
}

// Detail envelope from GET /admin/users/{id}.
export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    display_name: string | null;
    email_verified: boolean;
    is_superuser: boolean;
    suspended_at: string | null;
    suspended_reason: string | null;
    deleted_at: string | null;
    created_at: string;
    last_login_at: string | null;
  };
  org: {
    id: string;
    name: string;
    plan: PlanKey;
    quota_used_this_period: number;
    quota_period_cap: number | null;
    quota_lifetime_used: number;
  };
  counts: {
    datasets: number;
    discoveries: number;
    knowledge: number;
    api_keys: number;
  };
  recent_discoveries: Array<{
    id: string;
    status: string;
    title: string | null;
    created_at: string;
  }>;
  recent_activity: Array<{
    action: string;
    created_at: string;
    metadata: Record<string, unknown> | null;
  }>;
}

export async function fetchAdminUser(id: string): Promise<AdminUserDetail> {
  const resp = await api.get<AdminUserDetail>(`/admin/users/${id}`);
  return resp.data;
}

export async function suspendUser(id: string, reason: string): Promise<void> {
  await api.post(`/admin/users/${id}/suspend`, { reason });
}

export async function unsuspendUser(id: string): Promise<void> {
  await api.post(`/admin/users/${id}/unsuspend`);
}

export async function changeUserPlan(
  id: string,
  plan: PlanKey,
  periodCap?: number | null,
): Promise<void> {
  const body: { plan: PlanKey; period_cap?: number | null } = { plan };
  if (periodCap !== undefined) body.period_cap = periodCap;
  await api.patch(`/admin/users/${id}/plan`, body);
}

export async function resendVerification(id: string): Promise<void> {
  await api.post(`/admin/users/${id}/resend-verification`);
}

export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/admin/users/${id}`);
}

export interface ImpersonateOut {
  access_token: string;
  token_type: 'bearer';
  expires_at: string;
  user: {
    id: string;
    email: string;
    display_name: string | null;
  };
}

export async function impersonateUser(id: string): Promise<ImpersonateOut> {
  const resp = await api.post<ImpersonateOut>(`/admin/users/${id}/impersonate`);
  return resp.data;
}

// Server-side revoke of the CURRENT impersonation session (H2 kill-switch).
// MUST be called while the impersonation token is still the active token — the
// endpoint authenticates via that token's `act` claim and revokes its own jti
// in Redis, so the session is dead immediately (not just on TTL expiry).
// Best-effort: callers swallow errors (an already-expired token is moot).
export async function endImpersonationServer(): Promise<void> {
  await api.post('/admin/impersonation/end');
}

// --- Support tickets (admin side) -----------------------------------------

// The status FILTER includes "all" (a pseudo-status); the persisted ticket
// status does not. Re-export TicketStatus so callers can import everything
// support-related from one place.
export type { TicketStatus } from '@/lib/support';

export type TicketStatusFilter = 'all' | TicketStatus;

// Row shape from GET /admin/tickets — same as the user list plus requester
// identity + org for triage.
export interface AdminTicketListItem {
  id: string;
  number: number;
  subject: string;
  status: TicketStatus;
  last_message_at: string;
  message_count: number;
  requester_email: string;
  org_id: string;
  created_at: string;
}

export interface AdminTicketsPage {
  items: AdminTicketListItem[];
  next_cursor: string | null;
}

export interface AdminTicketsQuery {
  status?: TicketStatusFilter;
  cursor?: string;
  limit?: number;
}

export async function fetchAdminTickets(params: AdminTicketsQuery): Promise<AdminTicketsPage> {
  const resp = await api.get<AdminTicketsPage>('/admin/tickets', {
    params: {
      status: params.status ?? 'all',
      cursor: params.cursor || undefined,
      limit: params.limit ?? 20,
    },
  });
  return resp.data;
}

export async function fetchAdminTicket(id: string): Promise<TicketDetail> {
  const resp = await api.get<TicketDetail>(`/admin/tickets/${id}`);
  return resp.data;
}

export async function replyToAdminTicket(id: string, body: string): Promise<void> {
  await api.post(`/admin/tickets/${id}/messages`, { body });
}

export async function setAdminTicketStatus(id: string, status: TicketStatus): Promise<void> {
  await api.patch(`/admin/tickets/${id}`, { status });
}
